import { Component, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { DialogService } from 'src/app/common/dialog/dialog.service';
import { ToastService } from 'src/app/common/service/toast.service';
import { UserProfileService } from 'src/app/common/service/user-profile.service';
import { DataSource } from '../../../model/data-source';
import { RequestCriteria } from '../../../model/request-criteria';
import { SaveMode } from '../../../model/save-mode';
import { SetOfFilters } from '../../model/set-of-filters';
import { SetsOfFiltersService } from '../../service/sets-of-filters.service';
import { SetOfFiltersDialogComponent } from '../../component/dialog/set-of-filters-dialog/set-of-filters-dialog.component';
import { SelectWithDeleteOptionComponent } from '../../../component/select-with-delete-option/select-with-delete-option.component';
import { FilterBuilderService } from '../../service/filter-builder.service';
import { ENTITY_ALREADY_EXISTS_ERROR_KEY, ENTITY_NOT_FOUND_ERROR_KEY, hasError, INSUFFICIENT_PERMISSIONS_ERROR_KEY } from '../../../util/rest-service-util';
import { ValuesGroup } from '../../../model/values-group';
import { cloneDeep } from 'lodash';
import { createValuesGroupForGeneralSetOfFilters, createValuesGroupForUserSetOfFilters } from '../../../util/values-group-creator';
import { RequestCriteriaWithAdditionalInfo, toRequestCriteriaWithAdditionalInfo } from '../../../model/request-criteria-with-additional-info';
import { SetOfFiltersType } from '../../model/set-of-filters-type';

@Component({
  selector: 'app-set-of-filters',
  templateUrl: './set-of-filters.component.html',
  styleUrls: ['./set-of-filters.component.scss']
})
export class SetOfFiltersComponent {

  @Input()
  groupNames: string[];
  @ViewChild('setOfFiltersList') setOfFiltersList: SelectWithDeleteOptionComponent<SetOfFilters>;
  dataSource: DataSource;
  setsOfFilters: SetOfFilters[] = [];
  groupedSetOfFilters: ValuesGroup<SetOfFilters>[];
  mode: SaveMode = SaveMode.ADD;
  loadingFilters: boolean;
  saveModeHelper: typeof SaveMode = SaveMode;

  constructor(private dialogService: DialogService, private matDialog: MatDialog, private toastService: ToastService,
              private setsOfFiltersService: SetsOfFiltersService, private userProfileService: UserProfileService,
              private filterBuilderService: FilterBuilderService) { }

  initSetsOfFilters(dataSource: DataSource): void {
    this.mode = SaveMode.ADD;
    this.dataSource = dataSource;
    this.loadingFilters = true;
    this.setOfFiltersList.selectValue(null);
    this.setsOfFilters = null;
    this.setsOfFiltersService.fetchSetsOfFilters(this.dataSource.name)
      .pipe(
        catchError(error => {
          this.showError(error, 'fetchSetsOfFiltersError');
          return of<SetOfFilters[]>();
        }),
        finalize(() => this.loadingFilters = false)
      )
      .subscribe(setsOfFilters => {
        this.setsOfFilters = setsOfFilters;
        this.groupedSetOfFilters = this.getGroupedSetOfFilters();
      });
  }

  saveSetOfFilters(): void {
    this.filterBuilderService.filters$.pipe(take(1)).subscribe(filters => this.onFiltersReceived(filters));
    this.filterBuilderService.askForFilters();
  }

  resetFilters(): void {
    const initialFilters = toRequestCriteriaWithAdditionalInfo(this.dataSource.initialFilters);
    this.filterBuilderService.setInitialFilters(initialFilters);
    this.setOfFiltersList.selectValue(null);
    this.mode = SaveMode.ADD;
  }

  onSetOfFiltersSelected(selectedSetOfFilters: SetOfFilters): void {
    this.mode = this.getMode(selectedSetOfFilters);
    const setOfFilters = this.getSetOfFiltersOrCopy(selectedSetOfFilters);

    let initialFilters: RequestCriteriaWithAdditionalInfo;
    if (setOfFilters) {
      initialFilters = toRequestCriteriaWithAdditionalInfo(setOfFilters.criteria, setOfFilters.readOnly);
      const userFieldNames = this.dataSource.getValueFields().map(field => field.fieldName || field.name);
      initialFilters.criteria = this.getCriteriaAvailableForUser(initialFilters.criteria, userFieldNames);
    } else {
      initialFilters = toRequestCriteriaWithAdditionalInfo(this.dataSource.initialFilters);
    }
    this.filterBuilderService.setInitialFilters(initialFilters);
  }

  deleteSetOfFilters(setOfFilters: SetOfFilters): void {
    this.dialogService.openConfirmationDialog('deleteSetOfFilters')
      .afterClosed()
      .pipe(
        filter(result => result),
        switchMap(() => this.setsOfFiltersService.deleteSetOfFilters(setOfFilters.id)),
        catchError((error) => {
          this.handleErrors(error, setOfFilters, 'setOfFiltersNotExists');
          return of();
        }),
      ).subscribe(() => {
        const index = this.setsOfFilters.indexOf(setOfFilters);
        this.setsOfFilters.splice(index, 1);
        this.groupedSetOfFilters = this.getGroupedSetOfFilters();
        if (!this.currentSetOfFilters)
          this.filterBuilderService.setInitialFilters(toRequestCriteriaWithAdditionalInfo(this.dataSource.initialFilters));
      });
  }

  isSaveButtonEnabled(): boolean {
    if (this.dataSource && this.setOfFiltersList)
      return !this.currentSetOfFilters || this.currentSetOfFilters.type != SetOfFiltersType.GENERAL || this.userProfileService.isService();
    return false;
  }

  getGroupedSetOfFilters(): ValuesGroup<SetOfFilters>[] {
    const groups: ValuesGroup<SetOfFilters>[] = [];

    const readOnlyFilters = this.setsOfFilters.filter(filters => filters.readOnly);
    if (readOnlyFilters.length > 0)
      groups.push(createValuesGroupForGeneralSetOfFilters(this.groupNames[0], readOnlyFilters, this.userProfileService.isService()));

    const filtersGeneralPurpose = this.setsOfFilters.filter(filters => filters.type == SetOfFiltersType.GENERAL);
    if (filtersGeneralPurpose.length > 0)
      groups.push(createValuesGroupForGeneralSetOfFilters(this.groupNames[1], filtersGeneralPurpose, this.userProfileService.isService()));

    const userFilters = this.setsOfFilters.filter(filters => filters.type == SetOfFiltersType.USER);
    if (userFilters.length > 0)
      groups.push(createValuesGroupForUserSetOfFilters(this.groupNames[2], userFilters));

    return groups;
  }

  private getCriteriaAvailableForUser(allCriteria: RequestCriteria[], userFieldNames: string[]): RequestCriteria[] {
    return allCriteria.filter(criteria => {
      if (criteria.criteria)
        return this.getCriteriaAvailableForUser(criteria.criteria, userFieldNames).length > 0;
      else
        return userFieldNames.indexOf(criteria.fieldName) != -1;
    });
  }

  private onFiltersReceived(filters?: RequestCriteria): void {
    if (!filters)
      return;

    const dataType = this.dataSource.name;
    if (dataType) {
      let setOfFilters = this.getSetOfFiltersOrCopy(this.currentSetOfFilters);
      if (setOfFilters)
        setOfFilters.criteria = filters;
      else
        setOfFilters = new SetOfFilters(dataType, filters);

      if (this.hasFields(setOfFilters.criteria))
        this.createOrEdit(setOfFilters);
      else
        this.toastService.showInfoToast('emptySetOfFilters');
    } else {
      console.error('Unknown set of filters data type: ', this.dataSource.name);
    }
  }

  private createOrEdit(setOfFilters: SetOfFilters): void {
    if (!setOfFilters.id) {
      const existingSetNames = this.setsOfFilters.map(sets => sets.name);
      this.openSetOfFiltersDialog(setOfFilters, existingSetNames, this.mode);
    } else {
      this.setsOfFiltersService.saveSetOfFilters(setOfFilters)
        .pipe(
          catchError(error => {
            this.handleErrors(error, setOfFilters, 'saveSetOfFiltersError');
            return of<SetOfFilters>();
          }),
        )
        .subscribe(() => this.toastService.showInfoToast('setOfFiltersSaved'));
    }
  }

  private openSetOfFiltersDialog(setOfFilters: SetOfFilters, existingSetNames: string[], saveMode: SaveMode): void {
    this.matDialog
    .open(SetOfFiltersDialogComponent, { data: { setOfFilters, existingSetNames, saveMode } })
    .afterClosed()
    .pipe(filter(result => result))
    .subscribe(savedSetOfFilters => {
      this.setsOfFilters.push(savedSetOfFilters);
      this.groupedSetOfFilters = this.getGroupedSetOfFilters();
      this.setsOfFilters.sort((set1, set2) => set1.name.localeCompare(set2.name));
      this.setOfFiltersList.selectValue(savedSetOfFilters);
      this.onSetOfFiltersSelected(savedSetOfFilters);
    });
  }

  private hasFields(criteria: RequestCriteria): boolean {
    if (criteria.fieldName) {
      return true;
    } else if (criteria.criteria && criteria.criteria.length > 0) {
      for (const nestedCriteria of criteria.criteria) {
        if (this.hasFields(nestedCriteria))
          return true;
      }
    } else {
      return false;
    }
  }

  private handleErrors(error: any, setOfFilters: SetOfFilters, defaultErrorMessage: string): void {
    const errorToDisplay = this.getErrorMessage(error, defaultErrorMessage);
    if (errorToDisplay != defaultErrorMessage) {
      const index = this.setsOfFilters.indexOf(setOfFilters);
      if (index != -1)
        this.setsOfFilters.splice(index, 1);
    }

    this.showError(error, errorToDisplay);
  }

  private getErrorMessage(error: any, defaultErrorMessage: string): string {
    if (hasError(error, ENTITY_NOT_FOUND_ERROR_KEY))
      return 'setOfFiltersNotExists';
    if (hasError(error, ENTITY_ALREADY_EXISTS_ERROR_KEY))
      return 'setOfFiltersAlreadyExists';
    if (hasError(error, INSUFFICIENT_PERMISSIONS_ERROR_KEY))
      return 'UnauthorizedException';
    return defaultErrorMessage;
  }

  private getMode(setOfFilters: SetOfFilters): SaveMode {
    if (setOfFilters && setOfFilters.id)
      return setOfFilters.readOnly ? SaveMode.COPY : SaveMode.MODIFY;
    else
      return SaveMode.ADD;
  }

  private getSetOfFiltersOrCopy(setOfFilters: SetOfFilters): SetOfFilters {
    if (this.mode == SaveMode.COPY) {
      const copy = cloneDeep(setOfFilters);
      copy.id = null;
      return copy;
    } else {
      return setOfFilters;
    }
  }

  private showError(errorToLog: any, errorToDisplay: string): void {
    console.error(errorToLog);
    this.toastService.showErrorToast(errorToDisplay);
  }

  get currentSetOfFilters(): SetOfFilters {
    return this.setOfFiltersList.currentValue;
  }
}
